import { useIsDemo } from "@/demo/DemoContext";
import { PermissionGuard } from "@/components/PermissionGuard";
import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  Inbox,
  Mail,
  MoreVertical,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { toastApiError } from "@/lib/api-error";
import { formatDate, formatDateTime } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState } from "@/components/PageUi";
import { cn } from "@/lib/utils";
import { BackLink, PageContainer, PageHeader } from "@/components/PagePrimitives";
import type { components } from "@dolfin/api-public";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type GmailConnectionResponse = components["schemas"]["GmailConnectionResponse"];
type GmailConnectionStatus = components["schemas"]["GmailConnectionStatus"];

// A dot and a word, like the accounting connection: a filled badge would shout over the panel.
function StatusLine({ status }: { status: GmailConnectionStatus }) {
  const meta: Record<string, { dot: string; text: string; label: string }> = {
    Connected: { dot: "bg-emerald-500", text: "text-emerald-600", label: "Connected" },
    NeedsReauth: { dot: "bg-amber-500", text: "text-amber-600", label: "Needs reconnecting" },
    Backfilling: { dot: "bg-accent-blue animate-pulse", text: "text-accent-blue", label: "Importing past mail" },
    Revoked: { dot: "bg-muted-foreground", text: "text-muted-foreground", label: "Disconnected" },
  };
  const m = meta[status] ?? { dot: "bg-muted-foreground", text: "text-muted-foreground", label: status };
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium", m.text)}>
      <span className={cn("size-1.5 rounded-full", m.dot)} /> {m.label}
    </span>
  );
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "Never";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "Just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  return formatDateTime(iso);
}

export interface GmailSettingsPageProps {
  /** Open the candidate review queue for a connection. The host routes to its own candidates page. */
  onOpenCandidates: (connectionId: string) => void;
  /** Back affordance target (e.g. the bills list). Omit to hide the back link. */
  onBack?: () => void;
  /** Render the "use plain forwarding instead" hint, wired to the host's email-forwarding route. Omit to hide it. */
  onOpenEmailForwarding?: () => void;
  /**
   * Where Dolfin's fixed OAuth callback 302s back to (Google never sees it). Must render `GmailOAuthCallbackPage` and
   * be on the client's `GmailAllowedRedirectHosts`. Defaults to `${window.location.origin}/oauth/gmail/callback`.
   */
  returnUrl?: string;
}

export function GmailSettingsPage({
  onOpenCandidates,
  onBack,
  onOpenEmailForwarding,
  returnUrl,
}: GmailSettingsPageProps) {
  const isDemo = useIsDemo();
  const api = useDolfinClient();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [connections, setConnections] = useState<GmailConnectionResponse[]>([]);
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<Record<string, boolean>>({});
  const [pendingDisconnect, setPendingDisconnect] = useState<GmailConnectionResponse | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  const loadConnections = useCallback(async () => {
    const { data, error } = await api.GET("/v1/gmail-connections", {});
    if (error) {
      toastApiError(error, "Failed to load Gmail connections");
      setFailed(true);
      return;
    }
    setFailed(false);
    setConnections(data ?? []);
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadConnections().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loadConnections]);

  async function handleConnect(reconnectId?: string) {
    setConnectingId(reconnectId ?? "new");
    const resolvedReturnUrl =
      returnUrl ?? `${window.location.origin}/oauth/gmail/callback`;
    const { data, error } = await api.POST("/v1/gmail-connections", {
      body: { returnUrl: resolvedReturnUrl },
    });
    if (error || !data) {
      toastApiError(error, "Failed to start Gmail connection");
      setConnectingId(null);
      return;
    }
    window.location.href = data.authUrl;
  }

  async function handleSync(connection: GmailConnectionResponse) {
    setSyncing((s) => ({ ...s, [connection.id]: true }));
    const { error } = await api.POST("/v1/gmail-connections/{id}/sync", {
      params: { path: { id: connection.id } },
    });
    if (error) {
      toastApiError(error, "Failed to start sync");
    } else {
      toast.success("Sync started - new candidates will appear shortly.");
    }
    setSyncing((s) => ({ ...s, [connection.id]: false }));
  }

  async function handleDisconnect() {
    if (!pendingDisconnect) return;
    setDisconnecting(true);
    const { error } = await api.DELETE("/v1/gmail-connections/{id}", {
      params: { path: { id: pendingDisconnect.id } },
    });
    if (error) {
      toastApiError(error, "Failed to disconnect");
      setDisconnecting(false);
      return;
    }
    toast.success("Mailbox disconnected.");
    setPendingDisconnect(null);
    setDisconnecting(false);
    await loadConnections();
  }

  const activeConnections = connections.filter((c) => c.status !== "Revoked");

  const retry = () => {
    setLoading(true);
    void loadConnections().finally(() => setLoading(false));
  };

  return (
    <PageContainer>
      {onBack && <BackLink onClick={onBack}>Back to bills</BackLink>}
      <PageHeader
        eyebrow="Payables"
        title="Gmail integration"
        subtitle="Connect a Gmail mailbox and Dolfin pulls bill attachments straight from the inbox for you to review. Access is read-only; Dolfin never sends mail on your behalf."
        actions={
          !loading && !failed && activeConnections.length > 0 ? (
            <PermissionGuard allowed={!isDemo} reason="Not available in a demo">
              <Button variant="outline" size="sm" onClick={() => handleConnect()} disabled={connectingId !== null}>
                {connectingId === "new" ? <Spinner className="size-4" /> : <Plus className="size-4" />}
                Add mailbox
              </Button>
            </PermissionGuard>
          ) : undefined
        }
      />

      {loading ? (
        <LoadingState label="Loading mailboxes..." />
      ) : failed ? (
        <ErrorState message="Your Gmail connections could not be loaded." onRetry={retry} />
      ) : (
        <div className="space-y-4">
          {activeConnections.length === 0 ? (
            <EmptyState
              icon={Mail}
              title="No mailbox connected"
              action={
                <PermissionGuard allowed={!isDemo} reason="Not available in a demo">
                  <Button onClick={() => handleConnect()} disabled={connectingId !== null}>
                    {connectingId === "new" ? <Spinner className="size-4" /> : <Plus className="size-4" />}
                    Connect Gmail
                  </Button>
                </PermissionGuard>
              }
            >
              Dolfin scans the inbox for attachments that look like bills and lists them for you to review
              before anything becomes a bill.
            </EmptyState>
          ) : (
            activeConnections.map((c) => (
              <ConnectionCard
                key={c.id}
                connection={c}
                isSyncing={!!syncing[c.id]}
                isReconnecting={connectingId === c.id}
                onReconnect={() => handleConnect(c.id)}
                onSync={() => handleSync(c)}
                onDisconnect={() => setPendingDisconnect(c)}
                onOpenQueue={() => onOpenCandidates(c.id)}
              />
            ))
          )}

          {onOpenEmailForwarding && (
            <p className="pt-2 text-center text-xs text-muted-foreground">
              Looking for plain forwarding instead?{" "}
              <button type="button" onClick={onOpenEmailForwarding} className="underline underline-offset-2">
                Use the inbound email address
              </button>
              .
            </p>
          )}
        </div>
      )}

      <AlertDialog
        open={pendingDisconnect !== null}
        onOpenChange={(open) => !open && !disconnecting && setPendingDisconnect(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {pendingDisconnect?.emailAddress}?</AlertDialogTitle>
            <AlertDialogDescription>
              Dolfin will stop polling this mailbox and revoke its refresh token at Google.
              Candidates already imported into Bills will not be affected. You can reconnect at any
              time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnecting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDisconnect();
              }}
              disabled={disconnecting}
            >
              {disconnecting && <Spinner className="size-4" />}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}

function ConnectionCard({
  connection,
  isSyncing,
  isReconnecting,
  onReconnect,
  onSync,
  onDisconnect,
  onOpenQueue,
}: {
  connection: GmailConnectionResponse;
  isSyncing: boolean;
  isReconnecting: boolean;
  onReconnect: () => void;
  onSync: () => void;
  onDisconnect: () => void;
  onOpenQueue: () => void;
}) {
  const isDemo = useIsDemo();
  const isConnected = connection.status === "Connected";
  const needsReauth = connection.status === "NeedsReauth";
  const isBackfilling = connection.status === "Backfilling";

  return (
    <section className="rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-xl border bg-muted/50">
            <Mail className="size-5 text-muted-foreground" />
          </span>
          <div className="min-w-0">
            <div className="truncate font-semibold">{connection.emailAddress || "Gmail account"}</div>
            <StatusLine status={connection.status} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={onOpenQueue} size="sm">
            <Inbox className="size-4" />
            Review candidates
          </Button>
          <PermissionGuard allowed={!isDemo} reason="Not available in a demo">
            <Button variant="outline" size="sm" onClick={onSync} disabled={isSyncing || !isConnected}>
              {isSyncing ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />}
              Sync now
            </Button>
          </PermissionGuard>
          {isDemo ? null : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8" aria-label="More actions">
                <MoreVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onDisconnect} className="text-destructive focus:text-destructive">
                <Trash2 className="size-4" />
                Disconnect
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border @2xl:grid-cols-3">
        <Stat label="Last synced" value={relativeTime(connection.lastSyncedAt)} />
        <Stat
          label="History imported from"
          value={connection.backfilledSince ? formatDate(connection.backfilledSince) : isBackfilling ? "In progress" : "-"}
        />
        <Stat label="Connected" value={formatDate(connection.createdAt)} />
      </dl>

      {needsReauth ? (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
          <AlertTriangle className="size-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="min-w-0 flex-1 text-amber-900 dark:text-amber-200">
            Google stopped accepting this connection. Reconnect to resume pulling in bills.
          </p>
          <PermissionGuard allowed={!isDemo} reason="Not available in a demo">
            <Button size="sm" onClick={onReconnect} disabled={isReconnecting}>
              {isReconnecting && <Spinner className="size-4" />}
              Reconnect
            </Button>
          </PermissionGuard>
        </div>
      ) : isBackfilling ? (
        <p className="mt-4 text-xs text-muted-foreground">
          Importing past mail. Open the review queue to watch results arrive.
        </p>
      ) : null}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="bg-card px-4 py-3">
      <dt className="text-[10px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">{label}</dt>
      <dd className="mt-1 text-sm font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
